import { notFound } from 'next/navigation';
import { Locale } from '@/i18n/routing';
import apiClient from '@/apiClient';

export interface IFilterOptions {
  documentId: string;
  id: number;
  name: string;
  locale: Locale;
  slug: string;
}

export interface Pagination {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
}

export interface IngredientsData {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  slug: string;
  ingredientCategories: {
    id: number;
    name: string;
    slug: string;
    locale?: Locale;
  }[],
  links: {
    title: string,
    link: string,
  }[],
  locale?: Locale;
}

export interface IngredientsResponse {
  data: IngredientsData[];
  meta: {
    pagination: Pagination;
  };
}

export const getIngredientsPage = async (): Promise<any | null> => {
  try {
    return await apiClient.get('ingredients-page', {
      cache: 'no-cache',
    });
  } catch (e) {
    console.error(e.message);
    if (e.message.includes('404')) {
      notFound();
    }
    return null;
  }
};

export const getIngredient = async (slug: string, locale: Locale): Promise<any | null> => {
  try {
    return await apiClient.get(`ingredients/${slug}?locale=${locale}`, {
      cache: 'no-cache',
    });
  } catch (e) {
    console.error(e.message);
    if (e.message.includes('404')) {
      notFound();
    }
    return null;
  }
};

export const getIngredientCategories = async (locale: Locale): Promise<{
  data: IFilterOptions[], meta: { pagination: Pagination }
} | null> => {
  try {
    return await apiClient.get(
      `ingredient-categories?local=${locale}`,
      { cache: 'no-cache' },
    );
  } catch (e) {
    console.error(e.message);
    return null;
  }
};

export const getIngredients = async ({
  locale,
  selectedFilters,
  page = 1,
  pageSize = 15,
}: {
  page?: number,
  pageSize?: number
  selectedFilters?: string[],
  locale: Locale,
}): Promise<IngredientsResponse | null> => {
  try {
    const query = new URLSearchParams({
      'pagination[page]': String(page),
      'pagination[pageSize]': String(pageSize),
      locale,
    });

    if (selectedFilters && selectedFilters?.length > 0) {
      selectedFilters.forEach((slug, index) => {
        query.append(`filters[ingredientCategories][slug][$in][${index}]`, slug);
      });
    }

    return await apiClient.get(
      `ingredients?${query.toString()}`,
      { cache: 'no-cache' },
    );
  } catch (e) {
    console.error(e.message);
    return null;
  }
};
